import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import qapi from "views_shared/app/javascript/api";
import { resolveApiUrl } from "views_shared/app/javascript/api/api";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import { Modal, Button, Form } from "react-bootstrap";
import ImageCropped from "views_shared/app/javascript/components/ImageCropped";
import { composeUrl, composeUrlForProfile, inboxPath, COMPOSE_BODY_PARAM } from "./my_inbox/composeUrl";
import mayStartConversations from "./my_inbox/mayStartConversations";

// Keep the counter hidden for short messages; reveal it once it's long enough to matter.
const CHAR_COUNTER_REVEAL_AT = 1000;
// Matches Interact::Message::BODY_MAX_LENGTH and Interact::Conversation::SUBJECT_MAX_LENGTH.
const MAX_MESSAGE_LENGTH = 5000;
const SUBJECT_MAX_LENGTH = 200;
// The body textarea sizes to its content's estimated rendered row count (clamped) rather than a
// fixed row count: a canned prefill (e.g. Request Sample's multi-paragraph template, initialBody)
// can run well past 3 lines, and a fixed rows={3} box hid the rest behind a scrollbar the reader
// had to notice and use. Counting newlines alone undercounts a long
// paragraph that wraps onto several visual lines at phone width, so each newline-delimited line
// contributes max(1, ceil(length / BODY_ROW_CHAR_ESTIMATE)) rows rather than a flat 1; wider
// viewports overestimate slightly, which BODY_ROWS_MAX absorbs. An empty/short message (the common
// case) still gets the compact BODY_ROWS_MIN box.
const BODY_ROWS_MIN = 3;
const BODY_ROWS_MAX = 10;
// Rough characters-per-row at phone width, used only to estimate textarea row count above.
const BODY_ROW_CHAR_ESTIMATE = 40;

// Messaging policy — who SHOULD be able to message whom. This is the intended matrix, not a
// description of what the code enforces today:
// - Never yourself: no message button on any profile or promo entity the viewer represents.
// - Reporters (type_media) may message promoter profiles, promo entities, and other reporters.
// - Promoters may message other promoters — and therefore promo entities, whose messages reach
//   their promoter representatives — but never reporters (no DMing media).
// - Media outlets are not messageable by anyone.
// This component can't see the viewer/target relationship, so the render gate has to be the
// caller's. Only the Product page gates on it today (rail Connect card: authenticated
// non-representatives only); other call sites render unconditionally, and the mailbox routing in
// sendMessage does not implement the promoter-to-media restriction. Bringing the remaining callers
// and the routing in line is open work.
// Policy statement also in components/interact/AGENTS.md ("Messaging policy").
// The composer also serves callers with a canned message to send — a sample request, say — via the
// initial subject/body and the trigger's label and icon. Everything else about the send is the same.

// Deep-link into an existing inbox thread. An optional canned body (Request Sample) is carried along
// so the thread's composer opens pre-filled — the reporter still edits/sends it manually.
const inboxThreadUrl = (conversationId, body = null) => {
  const params = new URLSearchParams({ conversation: conversationId });
  if (body) params.set(COMPOSE_BODY_PARAM, body);
  return `${inboxPath()}?${params.toString()}`;
};
function MessageButton({
  profileId = null,
  promoEntityId = null,
  promoEntityType = null,
  extraButtonClasses = "mb-2 me-2",
  buttonLabel = "Message",
  buttonClassName = "btn btn-primary",
  // Two icon props for one icon (merge legacy): iconClassName wins when given and supports an
  // explicit falsy value = render no icon; buttonIconClasses is the older name some callers use.
  iconClassName = undefined,
  buttonIconClasses = "fa-solid fa-comment-dots",
  buttonStyle = null,
  // Opt-in (source directory): take the conversation into the inbox rather than a compose modal.
  // An existing thread opens directly; a brand-new one opens the inbox in compose mode where the
  // first message is authored inline in the real composer (the conversation is created on that
  // first send — a thread can't exist without a message). The modal is only used for the non-opt-in
  // direct-profile path below.
  openInboxThread = false,
  // Optional compose prefill (Request Sample): a canned body and a subject override carried into the
  // inbox composer for a brand-new thread. Only used on the openInboxThread compose path; when an
  // existing thread is found we deep-link into it and let the user type there (no prefill).
  composeBody = null,
  composeSubject = null,
  // Modal-path prefill (Request Sample via the compose modal): seeds the modal's subject/body.
  initialSubject = "",
  initialBody = "",
}) {
  const [showModal, setShowModal] = useState(false);
  const [hasOpenedModal, setHasOpenedModal] = useState(false);
  const [resolvingThread, setResolvingThread] = useState(false);
  const [messagedProfile, setMessagedProfile] = useState(null);
  const [receiverProfiles, setReceiverProfiles] = useState([]);
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [sending, setSending] = useState(false);
  // Synchronous double-submit guard. setSending is async, so disabling the button on `sending` alone
  // doesn't commit before a same-tick second click; a ref updates synchronously and closes that race.
  const sendingRef = useRef(false);

  const charCount = body.length;
  const overLimit = charCount > MAX_MESSAGE_LENGTH;
  const estimatedRows = body
    .split("\n")
    .reduce((total, line) => total + Math.max(1, Math.ceil(line.length / BODY_ROW_CHAR_ESTIMATE)), 0);
  const bodyRows = Math.min(BODY_ROWS_MAX, Math.max(BODY_ROWS_MIN, estimatedRows));

  const sendMessage = () => {
    if (sendingRef.current) {
      return; // synchronous double-submit guard (ref, not async state)
    }
    // With a prefilled body the form is submittable the instant it opens, which can beat the
    // asynchronous recipient lookup — and a send with no receivers is a guaranteed 422.
    if (!body.trim() || overLimit || receiverProfiles.length === 0) {
      return;
    }

    sendingRef.current = true;
    setSending(true);

    // Build the payload and POST inside one promise chain so a *synchronous* throw (e.g.
    // messagedProfile still loading when a promoter sends) also lands in .catch/.finally and re-arms
    // the button, instead of leaving sendingRef stuck true and Send permanently disabled.
    Promise.resolve()
      .then(() => {
        let mailbox = "other";

        // Reporters can message anyone in the "focused" mailbox
        if (globalThis.Qwoted.CURRENT_PROFILE_TYPE === "type_media") {
          mailbox = "focused";
        } else if (globalThis.Qwoted.CURRENT_PROFILE_TYPE.includes("promoter")) {
          // Promoters message media in the "other" mailbox
          if (messagedProfile.type_of_profile === "type_media") {
            // TODO: mailbox = "other" for promoters to media
            mailbox = "focused";
            // Promoters can message other promoters in the "focused" mailbox
          } else if (messagedProfile.type_of_profile.includes("promoter")) {
            mailbox = "focused";
          }
        }

        const receiverProfileIds = receiverProfiles.map((r) => r.slug);
        const attributes = {
          data: {
            attributes: {
              body,
              subject,
              mailbox,
              receiver_ids: receiverProfileIds,
            },
          },
        };

        // Add origin context if messaging about a promo entity (Expert/Product)
        // For direct profile-to-profile messaging, origin_type is optional
        if (promoEntityId && promoEntityType) {
          attributes.data.attributes.origin_id = promoEntityId;
          attributes.data.attributes.origin_type = promoEntityType;
        }

        // V1-COMPAT: resolveApiUrl no-ops in V2; when this file is copied verbatim into V1 it
        // crosses this Rails API call over to V2's origin instead of hitting V1 same-origin.
        return fetch(resolveApiUrl(`/interact/api/messaging/conversations`), {
          method: "POST",
          headers: new Headers({
            ...authenticatedHeadersHash(),
            "Content-Type": "application/json",
          }),
          body: JSON.stringify(attributes),
        });
      })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error("Network response was not ok.");
      })
      .then((responseJson) => {
        setShowModal(false);

        // Opt-in: land the sender in the freshly-created inbox thread instead of a toast. The create
        // response echoes the conversation row (see conversations_controller#render_success), so the
        // new conversation uuid is available for the inbox deep-link.
        const newConversationId = responseJson?.data?.attributes?.conversation_id;
        if (openInboxThread && newConversationId) {
          window.location.href = inboxThreadUrl(newConversationId);
          return;
        }

        const toastBody = `Sent message${messagedProfile && ` to ${messagedProfile.name}`}`;
        const toastLink = inboxPath();
        const messageObj = { toastTitle: "Sent message", toastLink, toastBody };
        globalThis.Qwoted.showToast(messageObj);
      })
      .catch(() => {
        globalThis.Qwoted.showToast({
          toastTitle: "Couldn't send message",
          toastBody: "Something went wrong — please try again.",
          variant: "warning",
        });
      })
      .finally(() => {
        sendingRef.current = false;
        setSending(false);
      });
  };

  useEffect(() => {
    if (!hasOpenedModal || profileId === null) {
      return;
    }
    const queryString = `${globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL}/rest/v1/user_profiles?profile_id=eq.${profileId}`;
    fetch(queryString, {
      method: "GET",
      headers: new Headers(authenticatedHeadersHash()),
    })
      .then((response) => response.json())
      .then((responseJson) => {
        if (responseJson.length > 0) {
          setMessagedProfile(responseJson[0]);
          if (promoEntityId === null) {
            setReceiverProfiles([responseJson[0]]);
          }
        }
      });
  }, [hasOpenedModal, profileId, promoEntityId]);

  useEffect(() => {
    if (!hasOpenedModal || promoEntityId === null) {
      return;
    }

    let queryString = `${globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL}/rest/v1/representations`;
    if (promoEntityType === "Id::PromoEntity::Company") {
      queryString = `${queryString}?promoted_company_id=eq.${promoEntityId}`;
    } else if (promoEntityType === "Id::PromoEntity::Product") {
      queryString = `${queryString}?promoted_entity_id=eq.${promoEntityId}`;
    } else if (promoEntityType === "Id::PromoEntity::Expert") {
      queryString = `${queryString}?promoted_entity_id=eq.${promoEntityId}`;
    } else {
      // TODO: throw error
    }

    fetch(queryString, {
      method: "GET",
      headers: new Headers(authenticatedHeadersHash()),
    })
      .then((response) => response.json())
      .then((responseJson) => {
        if (responseJson.length > 0) {
          const rProfiles = responseJson
            .map((r) => ({
              headline: r.promoter_job_title,
              name: r.promoter_profile_name,
              profile_image_url: r.promoter_profile_image_url,
              type_of_profile: "Id::Profile",
              slug: r.promoter_profile_slug,
            }))
            .filter((profile, index, array) => array.findIndex((p) => p.slug === profile.slug) === index);

          const rep1 = responseJson[0];
          const mProfile = {
            headline: promoEntityType === "Id::PromoEntity::Expert" && rep1.promoted_expert_job_title,
            name:
              promoEntityType !== "Id::PromoEntity::Company" ? rep1.promoted_entity_name : rep1.promoted_company_name,
            profile_image_url:
              promoEntityType !== "Id::PromoEntity::Company"
                ? rep1.promoted_entity_profile_image_url
                : rep1.promoted_company_profile_image_url,
            type_of_profile: promoEntityType,
            slug:
              promoEntityType !== "Id::PromoEntity::Company" ? rep1.promoted_entity_slug : rep1.promoted_company_slug,
          };

          setMessagedProfile(mProfile);
          setReceiverProfiles(rProfiles);
        }
      });
  }, [hasOpenedModal, promoEntityId, promoEntityType]);

  const renderReceiverProfiles = () => {
    if (receiverProfiles.length === 0) return null;

    // For a single profile, render the full profile information
    if (receiverProfiles.length === 1) {
      const receiverProfile = receiverProfiles[0];
      const descriptorBadge = null; // Placeholder for descriptor badge logic

      return (
        <div
          key={receiverProfile.id} // Assuming each profile has a unique identifier
          className="p-2 mb-3"
          style={{ border: "1px solid #ced4da", borderRadius: ".25rem", backgroundColor: "#e9ecef" }}
        >
          <div className="d-flex align-items-center">
            <ImageCropped
              roundedCircle
              wrapperClasses="me-2"
              src={receiverProfile.profile_image_url}
              alt={receiverProfile.headline}
              size={33}
            />
            <div>
              <strong>
                <a href={`/profiles/${receiverProfile.slug}`}>{receiverProfile.name}</a>
              </strong>
              <span className="ms-2">{descriptorBadge}</span>
              <br />
              <small>{receiverProfile.headline}</small>
            </div>
          </div>
        </div>
      );
    }
    const profileElements = receiverProfiles.reduce((acc, profile, index) => {
      const isLastProfile = index === receiverProfiles.length - 1;
      acc.push(
        <a key={profile.slug} href={`/profiles/${profile.slug}`}>
          {profile.name}
        </a>,
      );
      if (!isLastProfile) {
        acc.push(<span key={`${profile.slug}-comma`}>, </span>); // Add commas between elements
      }
      return acc;
    }, []);

    return (
      <div
        className="p-2 mb-3"
        style={{ border: "1px solid #ced4da", borderRadius: ".25rem", backgroundColor: "#e9ecef" }}
      >
        {profileElements}
      </div>
    );
  };

  const openComposeModal = () => {
    if (!hasOpenedModal) {
      setHasOpenedModal(true);
    }
    setShowModal(true);
  };

  // With openInboxThread, everything happens in the inbox. If the viewer already has a conversation
  // about this promo entity, jump straight into it; otherwise open the inbox in compose mode to
  // author the first message inline. The compose modal is never used on this path.
  const handleButtonClick = async () => {
    const currentProfileId = globalThis.Qwoted?.CURRENT_PROFILE_ID;
    if (openInboxThread && promoEntityId && promoEntityType && currentProfileId) {
      setResolvingThread(true);
      const result = await qapi.conversations
        .findLatestByOrigin({
          receiverProfileId: currentProfileId,
          originType: promoEntityType,
          originId: promoEntityId,
        })
        .catch(() => null);
      setResolvingThread(false);

      const existingId = result?.data?.[0]?.conversation_id;
      if (existingId) {
        // Pre-fill the existing thread's composer with the canned body too (Request Sample), so it's
        // populated whether or not a conversation already exists. composeBody is null for plain Message.
        window.location.href = inboxThreadUrl(existingId, composeBody);
        return;
      }

      window.location.href = composeUrl({
        originType: promoEntityType,
        originId: promoEntityId,
        body: composeBody,
        subject: composeSubject,
      });
      return;
    }

    // Profile-to-profile (PRs): no promo-entity origin, so dedup on the co-participant instead of
    // the origin. If the viewer already has a direct thread with this profile, deep-link into it;
    // otherwise open the inbox composer addressed directly to the recipient.
    if (openInboxThread && !promoEntityId && profileId) {
      if (currentProfileId) {
        setResolvingThread(true);
        const result = await qapi.conversations
          .findLatestPeerConversation({ receiverProfileId: currentProfileId, coParticipantProfileId: profileId })
          .catch(() => null);
        setResolvingThread(false);

        const existingId = result?.data?.[0]?.conversation_id;
        if (existingId) {
          window.location.href = inboxThreadUrl(existingId, composeBody);
          return;
        }
      }

      window.location.href = composeUrlForProfile({ profileId, body: composeBody, subject: composeSubject });
      return;
    }

    openComposeModal();
  };

  if (!mayStartConversations()) return null;

  const label = resolvingThread ? "Opening…" : buttonLabel;
  const resolvedIconClasses = iconClassName === undefined ? buttonIconClasses : iconClassName;

  return (
    <>
      <button
        type="button"
        className={`${buttonClassName} text-nowrap d-flex align-items-center justify-content-center gap-2 ${extraButtonClasses}`}
        style={buttonStyle}
        onClick={handleButtonClick}
        disabled={resolvingThread}
        aria-label={buttonLabel || "Message"}
      >
        {resolvedIconClasses && <i className={resolvedIconClasses} />}
        {label}
      </button>
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Message {messagedProfile?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="formTo">
            <Form.Label>To</Form.Label>
            {renderReceiverProfiles()}
          </Form.Group>

          <Form>
            <Form.Group className="mb-3" controlId="formSubject">
              <Form.Label>Subject</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={SUBJECT_MAX_LENGTH}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formBody">
              <Form.Label>Body</Form.Label>
              <Form.Control
                as="textarea"
                rows={bodyRows}
                placeholder="Enter message"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                isInvalid={overLimit}
              />
              {charCount >= CHAR_COUNTER_REVEAL_AT && (
                <div className="d-flex justify-content-end mt-1">
                  <span
                    className={classNames("small", overLimit ? "text-danger" : "text-muted")}
                    data-testid="char-count"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {charCount}/{MAX_MESSAGE_LENGTH}
                  </span>
                </div>
              )}
            </Form.Group>

            <Button
              variant="primary"
              onClick={sendMessage}
              disabled={!body.trim() || overLimit || sending || receiverProfiles.length === 0}
            >
              {sending ? "Sending…" : "Send"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default MessageButton;
